import api from './api';

export interface DeviceConnectRequest {
  deviceId?: number;
  ipAddress: string;
  port: number;
}

export interface FetchLogsRequest {
  deviceId: number;
  startDate?: string;
}

export interface SyncOptions {
  startDate?: string;
}

export interface ZKDeviceInfo {
  serialNumber: string;
  firmwareVersion: string;
  macAddress: string;
  platform: string;
  userCount: number;
  attendanceCount: number;
  fingerTemplateCount: number;
  deviceTime: string;
}

export interface AttendanceLog {
  employeeCode: string;
  checkTime: string;
  checkType: string;
  verifyMode: string;
}

export interface SyncResult {
  deviceId: number;
  deviceName: string;
  success: boolean;
  logsFound?: number;
  logsSaved?: number;
  error?: string;
}

export interface SyncAllResult {
  totalDevices: number;
  totalLogs: number;
  totalSaved: number;
  results: SyncResult[];
}

export interface SyncEmployeesToDeviceRequest {
  ipAddress?: string;
  port?: number;
  protocol?: string; // ZKTeco, Optex
  departmentId?: number;
  includeInactive?: boolean;
  dateFilter?: 'all' | 'today' | 'week'; // Zaman filtresi
  sessionId?: string; // SignalR progress icin
  // 2026-08-15: Puantaj ekranindaki gibi santiye/lokasyon filtresi
  siteId?: number;
  locationId?: number;
  // Dolu ise diger tum filtreler yok sayilir — yalnizca bu personeller gider
  employeeIds?: number[];
}

export interface SyncEmployeesResult {
  totalEmployees: number;
  successCount: number;
  failedCount: number;
  users: { enrollNumber: string; name: string }[];
}

export interface FetchLogsFromDeviceRequest {
  deviceId?: number;
  ipAddress: string;
  port?: number;
  protocol?: string; // ZKTeco, Optex
  startDate?: string;
}

export interface FetchLogsResult {
  count: number;
  saved: number;
  logs: AttendanceLog[];
}

const zktecoService = {
  // Cihaza baglan
  connect: async (request: DeviceConnectRequest): Promise<{ connected: boolean }> => {
    const response = await api.post<{ connected: boolean }>('/zkteco/connect', request);
    return response.data;
  },

  // Baglantıyı kes
  disconnect: async (): Promise<{ disconnected: boolean }> => {
    const response = await api.post<{ disconnected: boolean }>('/zkteco/disconnect');
    return response.data;
  },

  // Baglanti durumu
  getStatus: async (): Promise<{ isConnected: boolean; lastError: string | null }> => {
    const response = await api.get<{ isConnected: boolean; lastError: string | null }>('/zkteco/status');
    return response.data;
  },

  // Kayitlari al
  fetchLogs: async (request: FetchLogsRequest): Promise<{ count: number; saved: number; logs: AttendanceLog[] }> => {
    const response = await api.post<{ count: number; saved: number; logs: AttendanceLog[] }>('/zkteco/fetch-logs', request);
    return response.data;
  },

  // Cihaz bilgileri
  getDeviceInfo: async (): Promise<ZKDeviceInfo> => {
    const response = await api.get<ZKDeviceInfo>('/zkteco/device-info');
    return response.data;
  },

  // Cihazi yeniden baslat
  restartDevice: async (): Promise<{ restarted: boolean }> => {
    const response = await api.post<{ restarted: boolean }>('/zkteco/restart');
    return response.data;
  },

  // Tek cihazi senkronize et
  syncDevice: async (deviceId: number, options?: SyncOptions): Promise<{
    deviceId: number;
    deviceName: string;
    serialNumber: string;
    userCount: number;
    logsFound: number;
    logsSaved: number;
    syncTime: string;
  }> => {
    const response = await api.post<{
      deviceId: number;
      deviceName: string;
      serialNumber: string;
      userCount: number;
      logsFound: number;
      logsSaved: number;
      syncTime: string;
    }>(`/zkteco/sync/${deviceId}`, options || {});
    return response.data;
  },

  // Tum cihazlari senkronize et
  syncAllDevices: async (options?: SyncOptions): Promise<SyncAllResult> => {
    const response = await api.post<SyncAllResult>('/zkteco/sync-all', options || {});
    return response.data;
  },

  // Secili cihazlari senkronize et (SignalR progress ile)
  syncSelectedDevices: async (deviceIds: number[], options?: { startDate?: string; sessionId?: string }): Promise<{
    totalDevices: number;
    successCount: number;
    failedCount: number;
    totalLogs: number;
    totalSaved: number;
    results: SyncResult[];
  }> => {
    const response = await api.post<{
      totalDevices: number;
      successCount: number;
      failedCount: number;
      totalLogs: number;
      totalSaved: number;
      results: SyncResult[];
    }>('/zkteco/sync-selected', {
      deviceIds,
      startDate: options?.startDate,
      sessionId: options?.sessionId
    });
    return response.data;
  },

  // Cihaza personel gonder
  syncEmployeesToDevice: async (request: SyncEmployeesToDeviceRequest): Promise<SyncEmployeesResult> => {
    const response = await api.post<SyncEmployeesResult>('/zkteco/sync-employees-to-device', request);
    return response.data;
  },

  // Cihazdan veri oku (baglanti kur ve kayitlari al)
  fetchLogsFromDevice: async (request: FetchLogsFromDeviceRequest): Promise<FetchLogsResult> => {
    // Oncelikle baglan
    await api.post('/zkteco/connect', {
      ipAddress: request.ipAddress,
      port: request.port || (request.protocol === 'Optex' ? 5005 : 4370),
      protocol: request.protocol || 'ZKTeco'
    });

    // Sonra kayitlari al
    const response = await api.post<FetchLogsResult>('/zkteco/fetch-logs', {
      deviceId: request.deviceId,
      startDate: request.startDate,
      protocol: request.protocol || 'ZKTeco'
    });
    return response.data;
  },

  // Cihazdaki tum kullanicilari temizle
  clearDeviceUsers: async (request?: { ipAddress: string; port?: number; protocol?: string }): Promise<{ cleared: boolean; message?: string }> => {
    const response = await api.post<{ cleared: boolean; message?: string }>('/zkteco/clear-device-users', request || {});
    return response.data;
  },

  // Cihazdan tek kullanici sil
  deleteDeviceUser: async (enrollNumber: string): Promise<{ deleted: boolean }> => {
    const response = await api.delete<{ deleted: boolean }>(`/zkteco/device-user/${enrollNumber}`);
    return response.data;
  },

  // Cihazdaki kullanicilari listele
  getDeviceUsers: async (request?: { ipAddress: string; port?: number; protocol?: string }): Promise<{ users: { enrollNumber: string; name: string; cardNumber: string }[] }> => {
    const response = await api.post<{ users: { enrollNumber: string; name: string; cardNumber: string }[] }>('/zkteco/device-users', request || {});
    return response.data;
  },

  // Cihazdan birden fazla kullanici sil
  deleteDeviceUsers: async (request: { ipAddress: string; port?: number; enrollNumbers: string[] }): Promise<{ successCount: number; failedCount: number; totalRequested: number }> => {
    const response = await api.post<{ successCount: number; failedCount: number; totalRequested: number }>('/zkteco/delete-device-users', request);
    return response.data;
  },

  // Cihazdaki kullanicinin kart numarasini guncelle
  updateUserCard: async (request: { ipAddress: string; port?: number; enrollNumber: string; newCardNumber: string }): Promise<{ enrollNumber: string; newCardNumber: string }> => {
    const response = await api.post<{ enrollNumber: string; newCardNumber: string }>('/zkteco/update-user-card', request);
    return response.data;
  },

  // Cihaz teshis testi - baglanti, yazma, okuma testi yapar
  diagnoseDevice: async (request: { ipAddress: string; port?: number }): Promise<{ success: boolean; results: string[]; testUserFound: boolean }> => {
    const response = await api.post<{ success: boolean; results: string[]; testUserFound: boolean }>('/zkteco/diagnose', request);
    return response.data;
  }
};

export default zktecoService;
